import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppShareState } from '../Map.types';
import { LayerSelectorViews } from '../components/layerselector/LayerSelector.types';
import { ACTIVENOTIF_PARAM_NAME, LAYER_SELECTOR_PARAM_NAME } from '../MapV3';
import { OutletIProps } from '../../../components/sitebase/Base.types';
import { decodeShareState } from '../shareStateCodec';
import IS_EFAS from '../../../components/sitebase/Environment';

const defaultLayerSelectorView = IS_EFAS ? LayerSelectorViews.LIST : LayerSelectorViews.GROUP;

function resolveLayerSelectorView(view?: string | null): LayerSelectorViews {
    return LayerSelectorViews[view as keyof typeof LayerSelectorViews] || defaultLayerSelectorView;
}

export interface UseShareStateReturn {
    readonly state: AppShareState;
    updateCenter: (center: number[], zoom: number) => void;
    updateLayers: (layers: string) => void;
    updateForecast: (forecast: string) => void;
    updateActiveNotif: (active: string) => void;
    updateLayerSelectorView: (view: LayerSelectorViews) => void;
}

export function useShareState(context: OutletIProps): UseShareStateReturn {
    const [searchParams] = useSearchParams();

    const rawSearch = typeof window !== "undefined" ? window.location.search : "";
    const encodedState = rawSearch.startsWith("?") ? rawSearch.slice(1) : rawSearch;
    const parsedState = decodeShareState(encodedState) || decodeShareState(searchParams.get("state") || "");
    const initialLayerView = resolveLayerSelectorView(parsedState?.lview
        || searchParams.get(LAYER_SELECTOR_PARAM_NAME)
        || context.user.web_settings.layer_selector_view
        || null);

    const [state, setState] = useState<AppShareState>({
        c: parsedState?.c || searchParams.get('c') || '',
        z: parsedState?.z || searchParams.get('z') || '',
        layers: parsedState?.layers || searchParams.get("layers") || '',
        forecast: parsedState?.forecast || searchParams.get('forecast') || '',
        activenotif: parsedState?.activenotif || searchParams.get(ACTIVENOTIF_PARAM_NAME) || '',
        lview: initialLayerView,
        base_map: parsedState?.base_map || searchParams.get('base_map') || undefined,
    });

    const update = useCallback((updates: Partial<AppShareState>) => {
        setState(prev => {
            const newState = { ...prev, ...updates };
            return newState;
        });
    }, []);

    const updateCenter = (center: number[], zoom: number) => {
        update({
            c: center.join(","),
            z: zoom.toString()
        });
    }

    const updateLayers = (layers: string) => {
        update({ layers });
    }

    const updateForecast = (forecast: string) => {
        update({ forecast });
    }

    const updateActiveNotif = (active: string) => {
        update({ activenotif: active });
    }

    const updateLayerSelectorView = (view: LayerSelectorViews) => {
        update({ lview: view });
    }

    return {
        state,
        updateCenter,
        updateLayers,
        updateForecast,
        updateActiveNotif,
        updateLayerSelectorView,
    };
}
